import { MAX_SIGNAL_REPLY_TO_PREVIEW_LENGTH, continuitySchema, dispatchBriefSchema, entryBriefSchema, normalizeMessageEnvelope, resolveDetailedPublicAgentFailureReason as resolveDetailedPublicAgentFailureReason$1, resolveMappedPublicAgentFailureReason, resultArchiveModeSchema, wakeLoopSpaceActionSchema, wakeLoopSpaceEgressActionSchema } from "@wakeloop/schemas";
//#region src/agent-failure-reason.ts
function resolvePublicAgentFailureReason(input) {
	const errorCode = normalizeOptionalText$2(input.errorCode)?.toLowerCase();
	const detailedReason = resolveDetailedPublicAgentFailureReason(input.errorMessage);
	if (detailedReason) return detailedReason;
	if (!errorCode) return normalizeOptionalText$2(input.defaultReason);
	return resolveMappedPublicAgentFailureReason(input.errorCode, input.targetLabel) ?? normalizeOptionalText$2(input.defaultReason);
}
function resolveDetailedPublicAgentFailureReason(errorMessage) {
	return resolveDetailedPublicAgentFailureReason$1(errorMessage);
}
function normalizeOptionalText$2(value) {
	const normalized = typeof value === "string" ? value.trim() : "";
	return normalized.length > 0 ? normalized : null;
}
//#endregion
//#region src/agent-mental-model.ts
const WAKELOOP_AGENT_MENTAL_MODEL_LINES = [
	"WakeLoop: a user wakes an Agent Profile in a Space, the correct local WakeLoop Service carries the local run, and the Space shows a visible reply or an explicit visible failure.",
	"A Space is the shared collaboration surface; an Agent Profile is the AI teammate identity; WakeLoop Service is the local execution carrier, not the Space truth, profile identity, Wake availability authority, or final outcome owner.",
	"Default Collaboration mode keeps private agent work local. Publish the public outcome through WakeLoop Space Action; raw or transparent context sharing is explicit opt-in."
];
const WAKELOOP_AGENT_PUBLICATION_MODE_GUIDANCE_LINES = [
	"Collaboration mode is the default: agents publish concise public outcomes through WakeLoop Space Action rather than mirror all private work into the Space.",
	"Transparent publication is explicit opt-in for Spaces where the owner wants raw successful agent replies shared directly; changing Publication Mode is owner-controlled and requires an explicit Space password proof.",
	"The current Space contract decides the final dispatch rule. In a Wake, follow the dispatch prompt for that Space's Publication Mode."
];
function buildAgentFacingMentalModelLines() {
	return [...WAKELOOP_AGENT_MENTAL_MODEL_LINES];
}
function buildAgentFacingMentalModelParagraph() {
	return WAKELOOP_AGENT_MENTAL_MODEL_LINES.join(" ");
}
function buildAgentFacingPublicationModeGuideLines() {
	return [...WAKELOOP_AGENT_PUBLICATION_MODE_GUIDANCE_LINES];
}
//#endregion
//#region src/center-agent-role-template.ts
const CENTER_AGENT_ROLE_TEMPLATE_VERSION = "center-agent-role-template-v1";
const CENTER_AGENT_ROLE_TEMPLATE_LINES = [
	"You are this Organization's designated Center Agent.",
	"Your job is to help members turn a need into a working collaboration setup: create Spaces, set collaboration rules, create or enable Agent Profiles, invite members, and read organization directories.",
	"Your permissions are additive only. Do not delete Spaces, remove members, delete Agent Profiles, or perform destructive actions.",
	"Every privileged action must preserve attribution. When acting for a member, make it clear that you are acting on that member's behalf.",
	"Use the standard WakeLoop Space and Wake workflow. Do not invent a separate transport, private channel, or side system."
];
function buildCenterAgentRoleTemplateText() {
	return CENTER_AGENT_ROLE_TEMPLATE_LINES.map((line) => `- ${line}`).join("\n");
}
//#endregion
//#region src/continuity.ts
function deriveContinuity(input) {
	const parsed = continuitySchema.safeParse({
		messagesSinceThen: input.messagesSinceThen,
		higherAuthorityUpdates: input.higherAuthorityUpdateCount > 0
	});
	if (parsed.success) return parsed.data;
	throw new Error(parsed.error.issues[0]?.message ?? "continuity is invalid");
}
//#endregion
//#region src/dispatch-brief.ts
function composeDispatchBrief(input) {
	const parsed = dispatchBriefSchema.safeParse(input);
	if (parsed.success) return parsed.data;
	throw new Error(parsed.error.issues[0]?.message ?? "dispatch brief is invalid");
}
//#endregion
//#region src/entry-brief.ts
function composeEntryBrief(input) {
	const parsed = entryBriefSchema.safeParse(input);
	if (parsed.success) return parsed.data;
	throw new Error(parsed.error.issues[0]?.message ?? "entry brief is invalid");
}
//#endregion
//#region src/guide-entry.ts
function resolveGuideEntry(input) {
	if (input.source === "skill") {
		const label = normalizeOptionalText$1(input.label);
		return label ? `Use ${label} only if you need exact WakeLoop command details.` : "Use the WakeLoop guide only if you need exact command details.";
	}
	normalizeRequiredText(input.command, "command");
	return "Use the WakeLoop guide only if you need exact command details.";
}
function normalizeOptionalText$1(value) {
	const normalized = value?.trim() ?? "";
	return normalized.length > 0 ? normalized : null;
}
function normalizeRequiredText(value, label) {
	const normalized = value.trim();
	if (normalized.length > 0) return normalized;
	throw new Error(`${label} is required`);
}
//#endregion
//#region src/space-contract.ts
const SPACE_CONTRACT_ALLOWED_ACTION_ORDER = [
	"post_message",
	"post_status",
	"stay_silent"
];
const DEFAULT_SPACE_CONTRACT_FIELDS = {
	dispatchPolicy: { mode: "mention_only" },
	publicationMode: "collaboration",
	allowedActions: [...SPACE_CONTRACT_ALLOWED_ACTION_ORDER],
	resultArchivePolicy: { mode: "space_reply_only" },
	authorityOrder: "default",
	entryBrief: "default",
	dispatchBrief: "default"
};
function canonicalizeAllowedActions(actions) {
	const allowedActionSet = new Set(actions);
	return SPACE_CONTRACT_ALLOWED_ACTION_ORDER.filter((action) => allowedActionSet.has(action));
}
function areAllowedActionsEquivalent(left, right) {
	const canonicalLeft = canonicalizeAllowedActions(left);
	const canonicalRight = canonicalizeAllowedActions(right);
	return canonicalLeft.length === canonicalRight.length && canonicalLeft.every((action, index) => action === canonicalRight[index]);
}
function buildDefaultSpaceContract(input) {
	const updatedAt = input?.updatedAt ?? (/* @__PURE__ */ new Date()).toISOString();
	const updatedBy = normalizeUpdatedBy(input?.updatedBy);
	return {
		version: input?.version ?? 1,
		updatedAt,
		updatedBy,
		...DEFAULT_SPACE_CONTRACT_FIELDS
	};
}
function normalizeUpdatedBy(value) {
	const normalized = typeof value === "string" ? value.trim() : "";
	return normalized.length > 0 ? normalized : "system";
}
//#endregion
//#region src/publication-requirement.ts
const COLLABORATION_HOW_THIS_SPACE_WORKS = "WakeLoop is an external collaboration layer, not your private scratchpad. Work normally in your local context, then publish only the public outcome this Space needs with one WakeLoop Space Action.";
const COLLABORATION_ACTION_REQUIREMENT = "Final outcome: use exactly one WakeLoop Space Action through the CLI. Only if every Space Action CLI command fails, use one trailing wakeloop-space-action block.";
const COLLABORATION_REPAIR_REQUIREMENT = "Return the corrected final answer again, preserve the user's intent, and end with exactly one valid trailing ```wakeloop-space-action``` JSON block.";
const RAW_CONTEXT_SHARING_HOW_THIS_SPACE_WORKS = "This Space uses transparent context sharing: WakeLoop may share your raw reply directly. Use WakeLoop Space Action when you need a controlled visible outcome.";
const RAW_CONTEXT_SHARING_ACTION_REQUIREMENT = "Raw reply is allowed. Use WakeLoop Space Action only when you need a controlled visible outcome.";
const RAW_CONTEXT_SHARING_REPAIR_REQUIREMENT = "If you keep a trailing ```wakeloop-space-action``` block, it must be valid. Otherwise return the plain raw reply with no ```wakeloop-space-action``` block.";
function buildActionSchemaGuidanceLines(input) {
	const lines = ["Inside the block, JSON must use the key \"type\". Never use \"action\"."];
	for (const allowedAction of input.allowedActions) switch (allowedAction) {
		case "post_message":
			lines.push("Use post_message for normal public replies, greetings, answers, and discussion.");
			lines.push([
				"Canonical post_message block:",
				"```wakeloop-space-action",
				"{\"type\":\"post_message\",\"messageEnvelope\":{\"text\":\"Hello.\",\"activationTargets\":[],\"referenceTargets\":[],\"ccTargets\":[]}}",
				"```"
			].join("\n"));
			break;
		case "post_status":
			lines.push("Use post_status only for final-safe outcomes: done, blocked, needs_input, or handoff. Do not use \"working\" or \"ready\" because post_status closes the Wake.");
			lines.push(`Keep post_status text at ${MAX_SIGNAL_REPLY_TO_PREVIEW_LENGTH} characters or fewer. Use post_message for answers, discussion, or anything longer.`);
			lines.push([
				"Canonical post_status block (prefer final-safe statuses: done | blocked | needs_input | handoff):",
				"```wakeloop-space-action",
				"{\"type\":\"post_status\",\"status\":\"done\",\"text\":\"Status update.\"}",
				"```"
			].join("\n"));
			break;
		case "stay_silent":
			lines.push("Use stay_silent only when you intentionally do not want WakeLoop to publish a public reply.");
			lines.push([
				"Canonical stay_silent block:",
				"```wakeloop-space-action",
				"{\"type\":\"stay_silent\",\"reason\":\"No public reply is needed.\"}",
				"```"
			].join("\n"));
			break;
		default: break;
	}
	return lines;
}
function resolveSpacePublicationRequirement(input) {
	const publicationMode = input?.publicationMode ?? DEFAULT_SPACE_CONTRACT_FIELDS.publicationMode;
	const allowedActions = canonicalizeAllowedActions(input?.allowedActions ?? DEFAULT_SPACE_CONTRACT_FIELDS.allowedActions);
	if (publicationMode === "raw_context_sharing") return {
		actionRequirementLine: RAW_CONTEXT_SHARING_ACTION_REQUIREMENT,
		actionSchemaGuidanceLines: buildActionSchemaGuidanceLines({ allowedActions }),
		allowedActions,
		allowsRawReplyWithoutAction: true,
		howThisSpaceWorks: RAW_CONTEXT_SHARING_HOW_THIS_SPACE_WORKS,
		publicationMode,
		repairRequirementLine: RAW_CONTEXT_SHARING_REPAIR_REQUIREMENT,
		requiresExplicitPublicAction: false
	};
	return {
		actionRequirementLine: COLLABORATION_ACTION_REQUIREMENT,
		actionSchemaGuidanceLines: buildActionSchemaGuidanceLines({ allowedActions }),
		allowedActions,
		allowsRawReplyWithoutAction: false,
		howThisSpaceWorks: COLLABORATION_HOW_THIS_SPACE_WORKS,
		publicationMode,
		repairRequirementLine: COLLABORATION_REPAIR_REQUIREMENT,
		requiresExplicitPublicAction: true
	};
}
//#endregion
//#region src/result-archive.ts
function resolveResultArchiveBehavior(input) {
	const parsedMode = resultArchiveModeSchema.safeParse(input.contractMode);
	if (!parsedMode.success) throw new Error(parsedMode.error.issues[0]?.message ?? "result archive mode is invalid");
	const mode = resolveMode({
		contractMode: parsedMode.data,
		saveResult: input.saveResult
	});
	return {
		mode,
		shouldSave: mode !== "none"
	};
}
function resolveMode(input) {
	if (input.saveResult === false) return "none";
	return input.contractMode;
}
//#endregion
//#region src/system-notice.ts
const FALLBACK_ACTOR_NAME = "Agent";
const FALLBACK_TARGET_AGENT_NAME = "This agent";
function buildSystemNoticeText(input) {
	const normalizedActorName = normalizeOptionalText(input.actorName);
	const actorName = normalizedActorName ?? FALLBACK_ACTOR_NAME;
	const reason = normalizeOptionalText(input.reason);
	switch (input.noticeType) {
		case "stay_silent": return `${actorName} chose to stay silent.`;
		case "policy_blocked": return reason ? `WakeLoop blocked a public reply from ${actorName}: ${reason}` : `WakeLoop blocked a public reply from ${actorName}.`;
		case "terminal_failure": {
			const targetName = normalizedActorName ?? FALLBACK_TARGET_AGENT_NAME;
			if (input.variant === "delivery_expired") return `${targetName} did not receive this Wake before it expired. Wake the agent again when the local WakeLoop Service is online.`;
			if (input.variant === "progress_stale") return `${targetName} stopped making progress on this Wake. Reconnect WakeLoop Service before trying again.`;
			return reason ? `${actorName} couldn't reply: ${reason}` : `${actorName} couldn't reply.`;
		}
		case "wake_stopped":
			if (input.variant === "stopped_before_execution") return normalizedActorName ? `Wake was stopped for ${normalizedActorName} before it reached the agent.` : "Wake was stopped before it reached the agent.";
			return normalizedActorName ? `Wake was stopped for ${normalizedActorName}.` : "Wake was stopped.";
		default: return assertUnexpectedNoticeType(input.noticeType);
	}
}
function normalizeOptionalText(value) {
	const normalized = value?.trim() ?? "";
	return normalized.length > 0 ? normalized : null;
}
function assertUnexpectedNoticeType(value) {
	throw new Error(`unsupported system notice type: ${String(value)}`);
}
//#endregion
//#region src/wakeloop-space-action.ts
const STRUCTURED_ACTION_BLOCK_INFO_STRING = "wakeloop-space-action";
const STRUCTURED_ACTION_BLOCK_RE = /(?:\r?\n)?```wakeloop-space-action\s*\r?\n([\s\S]*?)\r?\n```\s*$/;
const STRUCTURED_ACTION_BLOCK_LINE_BREAK_RE = /\r?\n/u;
const STRUCTURED_ACTION_BLOCK_HEADER_PART_RE = /\s+/u;
const STRUCTURED_ACTION_BLOCK_PARSE_ERROR = "WakeLoop Space structured action block must be valid JSON or simple WakeLoop action syntax";
const TRAILING_JSON_ACTION_PARSE_ERROR = "WakeLoop Space trailing JSON action must be a valid JSON object";
function normalizeWakeLoopSpaceAction(input) {
	const parsed = wakeLoopSpaceActionSchema.safeParse(input);
	if (parsed.success) return parsed.data;
	throw new Error(parsed.error.issues[0]?.message ?? "WakeLoop Space Action is invalid");
}
function normalizeWakeLoopSpaceEgressActionInput(input) {
	if (!isRecord(input)) return input;
	const action = { ...input };
	switch (action.type) {
		case "post_message": return normalizePostMessageEgressActionInput(action);
		case "post_status": return normalizePostStatusEgressActionInput(action);
		case "stay_silent": return normalizeStaySilentEgressActionInput(action);
		default: return action;
	}
}
function hydrateWakeLoopSpaceEgressActionInputFromCleanedOutput(input) {
	if (!(input.action && typeof input.action === "object")) return input.action;
	const cleanedOutput = input.cleanedOutput.trim();
	if (!cleanedOutput) return input.action;
	const action = { ...input.action };
	if (action.type === "post_message" && typeof action.text !== "string" && typeof action.message !== "string") return {
		...action,
		text: cleanedOutput
	};
	if (action.type === "post_status" && typeof action.text !== "string" && typeof action.note !== "string") return {
		...action,
		text: cleanedOutput
	};
	return action;
}
function normalizeWakeLoopSpaceEgressAction(input) {
	const parsed = wakeLoopSpaceEgressActionSchema.safeParse(shouldSkipSharedPostMessageNormalization(input) ? input : normalizeWakeLoopSpaceEgressActionInput(input));
	if (parsed.success) return parsed.data;
	throw new Error(parsed.error.issues[0]?.message ?? "WakeLoop Space egress action is invalid");
}
function shouldSkipSharedPostMessageNormalization(input) {
	if (!(input && typeof input === "object" && !Array.isArray(input))) return false;
	const action = input;
	return action.type === "post_message" && !(action.messageEnvelope && typeof action.messageEnvelope === "object");
}
function extractTrailingStructuredSpaceActionBlock(input) {
	const extraction = tryExtractTrailingStructuredSpaceActionBlock(input);
	if (extraction.parseError) throw new Error(extraction.parseError);
	return {
		carrierKind: extraction.carrierKind,
		cleanedOutput: extraction.cleanedOutput,
		action: extraction.action,
		infoString: extraction.infoString
	};
}
function tryExtractTrailingStructuredSpaceActionBlock(input) {
	const rawOutput = typeof input.output === "string" ? input.output : "";
	const trimmedOutput = rawOutput.trimEnd();
	if (trimmedOutput.length === 0) return {
		carrierKind: "none",
		cleanedOutput: trimmedOutput,
		action: null,
		infoString: STRUCTURED_ACTION_BLOCK_INFO_STRING,
		parseError: null
	};
	let output;
	try {
		output = normalizeOutput(rawOutput);
	} catch (error) {
		return {
			carrierKind: "invalid_json",
			cleanedOutput: trimmedOutput,
			action: null,
			infoString: STRUCTURED_ACTION_BLOCK_INFO_STRING,
			parseError: error instanceof Error ? error.message : "WakeLoop Space structured action output is invalid"
		};
	}
	const matched = STRUCTURED_ACTION_BLOCK_RE.exec(output);
	if (!matched) return extractTrailingStandaloneJsonSpaceAction({
		output,
		infoString: STRUCTURED_ACTION_BLOCK_INFO_STRING
	});
	const cleanedOutput = resolveInvalidBlockFallbackOutput({
		matchedIndex: matched.index,
		output
	});
	const blockText = matched[1]?.trim() ?? "";
	if (blockText.length === 0) return {
		carrierKind: "fenced_block",
		cleanedOutput,
		action: null,
		infoString: STRUCTURED_ACTION_BLOCK_INFO_STRING,
		parseError: "WakeLoop Space structured action block is empty"
	};
	const parsedBlock = tryParseStructuredSpaceActionBlockInput(blockText);
	if (!(parsedBlock.actionInput && !parsedBlock.parseError)) return {
		carrierKind: "fenced_block",
		cleanedOutput,
		action: null,
		infoString: STRUCTURED_ACTION_BLOCK_INFO_STRING,
		parseError: parsedBlock.parseError
	};
	try {
		const action = normalizeExtractedWakeLoopSpaceEgressAction(hydrateWakeLoopSpaceEgressActionInputFromCleanedOutput({
			action: parsedBlock.actionInput,
			cleanedOutput
		}));
		return {
			carrierKind: "fenced_block",
			cleanedOutput: output.slice(0, matched.index).trimEnd(),
			action,
			infoString: STRUCTURED_ACTION_BLOCK_INFO_STRING,
			parseError: null
		};
	} catch (error) {
		return {
			carrierKind: "fenced_block",
			cleanedOutput,
			action: null,
			infoString: STRUCTURED_ACTION_BLOCK_INFO_STRING,
			parseError: error instanceof Error ? error.message : "WakeLoop Space egress action is invalid"
		};
	}
}
function extractTrailingStandaloneJsonSpaceAction(input) {
	const candidates = resolveTrailingStandaloneJsonActionCandidates(input.output);
	if (candidates.length === 0) return {
		carrierKind: "none",
		cleanedOutput: input.output,
		action: null,
		infoString: input.infoString,
		parseError: null
	};
	let parseError = TRAILING_JSON_ACTION_PARSE_ERROR;
	for (const candidate of candidates) {
		const parsedJson = parseStructuredSpaceActionBlockJson(candidate.jsonText);
		if (!(parsedJson && typeof parsedJson === "object" && !Array.isArray(parsedJson))) continue;
		try {
			const action = normalizeExtractedWakeLoopSpaceEgressAction(hydrateWakeLoopSpaceEgressActionInputFromCleanedOutput({
				action: parsedJson,
				cleanedOutput: candidate.cleanedOutput
			}));
			return {
				carrierKind: "trailing_json",
				cleanedOutput: candidate.cleanedOutput,
				action,
				infoString: input.infoString,
				parseError: null
			};
		} catch (error) {
			if (parseError === TRAILING_JSON_ACTION_PARSE_ERROR) parseError = error instanceof Error ? error.message : "WakeLoop Space egress action is invalid";
		}
	}
	return {
		carrierKind: "invalid_json",
		cleanedOutput: input.output,
		action: null,
		infoString: input.infoString,
		parseError
	};
}
function tryParseStructuredSpaceActionBlockInput(blockText) {
	const normalizedBlockText = blockText.trim();
	if (normalizedBlockText.length === 0) return {
		actionInput: null,
		parseError: "WakeLoop Space structured action block is empty"
	};
	const parsedJson = parseStructuredSpaceActionBlockJson(normalizedBlockText);
	if (parsedJson) return {
		actionInput: parsedJson,
		parseError: null
	};
	const parsedSimpleAction = parseSimpleStructuredSpaceActionBlock(normalizedBlockText);
	if (parsedSimpleAction) return {
		actionInput: parsedSimpleAction,
		parseError: null
	};
	return {
		actionInput: null,
		parseError: STRUCTURED_ACTION_BLOCK_PARSE_ERROR
	};
}
function normalizeOutput(value) {
	const normalized = value.trimEnd();
	if (normalized.length > 0) return normalized;
	throw new Error("output is required");
}
function resolveTrailingStandaloneJsonActionCandidates(output) {
	const candidateOutput = output.trimEnd();
	if (!candidateOutput.endsWith("}")) return [];
	return resolveTrailingStandaloneJsonLineStartIndices(candidateOutput).map((startIndex) => {
		const jsonText = candidateOutput.slice(startIndex).trim();
		return {
			cleanedOutput: candidateOutput.slice(0, startIndex).trimEnd(),
			jsonText
		};
	}).filter((candidate) => candidate.jsonText.startsWith("{") && candidate.jsonText.endsWith("}"));
}
function resolveTrailingStandaloneJsonLineStartIndices(output) {
	const lineStartIndices = [];
	let lineStartIndex = 0;
	for (let index = 0; index <= output.length; index += 1) {
		if (!(index === output.length || output[index] === "\n")) continue;
		let candidateStartIndex = lineStartIndex;
		while (candidateStartIndex < index && (output[candidateStartIndex] === " " || output[candidateStartIndex] === "	")) candidateStartIndex += 1;
		if (output[candidateStartIndex] === "{") lineStartIndices.push(candidateStartIndex);
		lineStartIndex = index + 1;
	}
	return lineStartIndices.reverse();
}
function resolveInvalidBlockFallbackOutput(input) {
	const cleanedOutput = input.output.slice(0, input.matchedIndex).trimEnd();
	return cleanedOutput.length > 0 ? cleanedOutput : "";
}
function parseStructuredSpaceActionBlockJson(blockText) {
	try {
		return JSON.parse(blockText);
	} catch {
		const repairedBlockText = appendMissingJsonClosers(blockText);
		if (!repairedBlockText || repairedBlockText === blockText) return null;
		try {
			return JSON.parse(repairedBlockText);
		} catch {
			return null;
		}
	}
}
function isRecord(value) {
	return Boolean(value && typeof value === "object" && !Array.isArray(value));
}
function normalizePostMessageEgressActionInput(action) {
	const { message, messageEnvelope, replyTo, text, ...rest } = action;
	if (isRecord(messageEnvelope)) return {
		...rest,
		messageEnvelope: normalizeMessageEnvelope(messageEnvelope)
	};
	const rawText = resolveLegacyPostMessageText({
		message,
		text
	});
	if (typeof rawText !== "string") return rest;
	const rawReplyToSignalId = resolveLegacyReplyToSignalId(replyTo);
	return {
		...rest,
		messageEnvelope: normalizeMessageEnvelope({
			...rawReplyToSignalId ? { replyToSignalId: rawReplyToSignalId } : {},
			text: rawText
		})
	};
}
function resolveLegacyPostMessageText(input) {
	if (typeof input.text === "string") return input.text;
	if (typeof input.message === "string") return input.message;
}
function resolveLegacyReplyToSignalId(value) {
	if (!isRecord(value) || typeof value.signalId !== "string") return;
	return value.signalId;
}
function normalizePostStatusEgressActionInput(action) {
	const { note, ...rest } = action;
	if (typeof rest.text !== "string" && typeof note === "string") return {
		...rest,
		text: note.trim()
	};
	return rest;
}
function normalizeStaySilentEgressActionInput(action) {
	const { note, ...rest } = action;
	if (typeof rest.reason !== "string" && typeof note === "string") return {
		...rest,
		reason: note.trim()
	};
	return rest;
}
function parseSimpleStructuredSpaceActionBlock(blockText) {
	const [rawHeader, ...bodyLines] = trimSimpleStructuredActionOuterEmptyLines(blockText.split(STRUCTURED_ACTION_BLOCK_LINE_BREAK_RE));
	const parsedHeader = parseSimpleStructuredActionHeader(rawHeader);
	if (!parsedHeader) return null;
	switch (parsedHeader.actionType) {
		case "post_message": return buildSimplePostMessageAction({
			bodyLines,
			headerBody: parsedHeader.headerBody
		});
		case "stay_silent": return buildSimpleStaySilentAction({
			bodyLines,
			headerBody: parsedHeader.headerBody
		});
		case "post_status": return buildSimplePostStatusAction({
			bodyLines,
			headerArgs: parsedHeader.headerArgs
		});
		default: return null;
	}
}
function parseSimpleStructuredActionHeader(rawHeader) {
	const header = rawHeader?.trim();
	if (!header) return null;
	const [rawActionType, ...headerArgs] = header.split(STRUCTURED_ACTION_BLOCK_HEADER_PART_RE);
	const actionType = rawActionType?.trim();
	if (!actionType) return null;
	return {
		actionType,
		headerArgs,
		headerBody: headerArgs.join(" ").trim()
	};
}
function buildSimplePostMessageAction(input) {
	const text = joinSimpleStructuredActionTextParts([input.headerBody, joinSimpleStructuredActionText(input.headerBody ? input.bodyLines : trimSingleLeadingEmptyLine(input.bodyLines))]);
	return text ? {
		type: "post_message",
		text
	} : { type: "post_message" };
}
function buildSimpleStaySilentAction(input) {
	const reason = joinSimpleStructuredActionTextParts([input.headerBody, joinSimpleStructuredActionText(input.headerBody ? input.bodyLines : trimSingleLeadingEmptyLine(input.bodyLines))]);
	return reason ? {
		type: "stay_silent",
		reason
	} : { type: "stay_silent" };
}
function buildSimplePostStatusAction(input) {
	const statusFromHeader = input.headerArgs[0]?.trim();
	const headerNote = input.headerArgs.slice(1).join(" ").trim();
	const bodyLinesAfterSingleSpacer = trimSingleLeadingEmptyLine(input.bodyLines);
	const status = statusFromHeader || bodyLinesAfterSingleSpacer[0]?.trim();
	if (!status) return null;
	let textLines;
	if (statusFromHeader) textLines = headerNote ? [headerNote, ...input.bodyLines] : trimSingleLeadingEmptyLine(input.bodyLines);
	else textLines = trimSingleLeadingEmptyLine(bodyLinesAfterSingleSpacer.slice(1));
	const text = joinSimpleStructuredActionText(textLines);
	return text ? {
		type: "post_status",
		status,
		text
	} : {
		type: "post_status",
		status
	};
}
function trimSimpleStructuredActionOuterEmptyLines(lines) {
	const nextLines = [...lines];
	if (nextLines[0]?.trim() === "") nextLines.shift();
	if (nextLines.at(-1)?.trim() === "") nextLines.pop();
	return nextLines;
}
function trimSingleLeadingEmptyLine(lines) {
	return lines[0]?.trim() === "" ? lines.slice(1) : lines;
}
function joinSimpleStructuredActionText(lines) {
	const text = lines.join("\n");
	return text.trim().length > 0 ? text : null;
}
function joinSimpleStructuredActionTextParts(parts) {
	const definedParts = parts.filter((part) => typeof part === "string" && part.trim().length > 0);
	if (definedParts.length === 0) return null;
	return definedParts.join("\n");
}
function normalizeExtractedWakeLoopSpaceEgressAction(input) {
	const normalizedInput = normalizeWakeLoopSpaceEgressActionInput(input);
	const parsed = wakeLoopSpaceEgressActionSchema.safeParse(normalizedInput);
	if (!parsed.success) throw new Error(parsed.error.issues[0]?.message ?? "WakeLoop Space egress action is invalid");
	if (!(normalizedInput && typeof normalizedInput === "object")) return parsed.data;
	if (parsed.data.type === "post_status") {
		const preservedText = normalizedInput.text;
		if (typeof preservedText === "string" && preservedText.trim().length > 0) return {
			...parsed.data,
			text: preservedText
		};
	}
	if (parsed.data.type === "post_message" && "messageEnvelope" in normalizedInput && normalizedInput.messageEnvelope && typeof normalizedInput.messageEnvelope === "object") {
		const preservedText = normalizedInput.messageEnvelope.text;
		if (typeof preservedText === "string" && preservedText.trim().length > 0) return {
			...parsed.data,
			messageEnvelope: {
				...parsed.data.messageEnvelope,
				text: preservedText
			}
		};
	}
	return parsed.data;
}
function appendMissingJsonClosers(blockText) {
	const stack = [];
	let inString = false;
	let escaped = false;
	for (const character of blockText) {
		if (escaped) {
			escaped = false;
			continue;
		}
		if (character === "\\") {
			escaped = true;
			continue;
		}
		if (character === "\"") {
			inString = !inString;
			continue;
		}
		if (inString) continue;
		if (character === "{") {
			stack.push("}");
			continue;
		}
		if (character === "[") {
			stack.push("]");
			continue;
		}
		if ((character === "}" || character === "]") && stack.at(-1) === character) stack.pop();
	}
	if (inString || stack.length === 0) return null;
	return `${blockText}${stack.reverse().join("")}`;
}
//#endregion
export { CENTER_AGENT_ROLE_TEMPLATE_LINES, CENTER_AGENT_ROLE_TEMPLATE_VERSION, DEFAULT_SPACE_CONTRACT_FIELDS, SPACE_CONTRACT_ALLOWED_ACTION_ORDER, WAKELOOP_AGENT_MENTAL_MODEL_LINES, WAKELOOP_AGENT_PUBLICATION_MODE_GUIDANCE_LINES, areAllowedActionsEquivalent, buildAgentFacingMentalModelLines, buildAgentFacingMentalModelParagraph, buildAgentFacingPublicationModeGuideLines, buildCenterAgentRoleTemplateText, buildDefaultSpaceContract, buildSystemNoticeText, canonicalizeAllowedActions, composeDispatchBrief, composeEntryBrief, deriveContinuity, extractTrailingStructuredSpaceActionBlock, hydrateWakeLoopSpaceEgressActionInputFromCleanedOutput, normalizeWakeLoopSpaceAction, normalizeWakeLoopSpaceEgressAction, normalizeWakeLoopSpaceEgressActionInput, resolveDetailedPublicAgentFailureReason, resolveGuideEntry, resolvePublicAgentFailureReason, resolveResultArchiveBehavior, resolveSpacePublicationRequirement, tryExtractTrailingStructuredSpaceActionBlock, tryParseStructuredSpaceActionBlockInput };

//# sourceMappingURL=index.js.map